import { Button, type ButtonProps } from '@mantine/core';
import { useTheme } from '@contexts/ThemeContext';
import { forwardRef, type ReactNode } from 'react';

export interface JarvisPrimaryButtonProps
  extends Omit<ButtonProps, 'leftSection' | 'rightSection' | 'onClick' | 'type'> {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onClick?: () => void | Promise<void>;
  type?: 'button' | 'submit' | 'reset';
}

/**
 * JarvisPrimaryButton - A themed primary Button component that automatically applies
 * the current theme colors to match the application's design system.
 *
 * Uses shadcn CSS vars (--primary / --primary-foreground) so contrast stays correct:
 * light → dark fill + light text; dark/jarvis → light fill + dark text.
 *
 * Usage: Use this for primary actions (submit, save, confirm, etc.)
 *
 * Props:
 * - leftIcon: ReactNode - Icon to display on the left side of the button
 * - rightIcon: ReactNode - Icon to display on the right side of the button
 * - All other Mantine Button props are supported
 */
export const JarvisPrimaryButton = forwardRef<HTMLButtonElement, JarvisPrimaryButtonProps>(
  ({ leftIcon, rightIcon, type, className, ...props }, ref) => {
    const { mode, colors: themeColors } = useTheme();
    const isJarvis = mode === 'jarvis';
    const mergedClassName =
      ['bg-primary', 'text-primary-foreground', isJarvis ? 'jarvis-primary-btn' : undefined, className]
        .filter(Boolean)
        .join(' ') || undefined;

    return (
      <Button
        {...props}
        ref={ref}
        type={type}
        variant="filled"
        className={mergedClassName}
        leftSection={leftIcon}
        rightSection={rightIcon}
        styles={{
          root: {
            // Prefer shadcn CSS vars (ThemeContext syncs these from palette tokens)
            backgroundColor: 'hsl(var(--primary))',
            color: 'hsl(var(--primary-foreground))',
            border: '1px solid hsl(var(--primary))',
            borderRadius: '10px',
            ...(isJarvis
              ? {
                  position: 'relative',
                  overflow: 'hidden',
                  isolation: 'isolate',
                  backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #f4f4f5 100%)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 2px rgba(0,0,0,0.28)',
                  transition:
                    'background-color 160ms ease, background-image 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                  '&:hover': {
                    backgroundColor: themeColors.buttonHover,
                    backgroundImage: 'linear-gradient(180deg, #fafafa 0%, #e4e4e7 100%)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55), 0 4px 14px rgba(0,0,0,0.35)',
                    transform: 'translateY(-1px)',
                  },
                }
              : {
                  '&:hover': {
                    backgroundColor: themeColors.buttonHover,
                  },
                }),
            '&:disabled': {
              backgroundColor: 'hsl(var(--primary))',
              backgroundImage: 'none',
              opacity: 0.5,
              color: 'hsl(var(--primary-foreground))',
              transform: 'none',
              boxShadow: 'none',
            },
          },
          section: {
            color: 'hsl(var(--primary-foreground))',
          },
        }}
      />
    );
  }
);

JarvisPrimaryButton.displayName = 'JarvisPrimaryButton';

export default JarvisPrimaryButton;
